'use client'

import * as Sentry from '@sentry/nextjs'
import { useEffect } from 'react'

/**
 * Last-resort boundary: catches a crash in the root layout or in the app's
 * own error boundaries, where Next would otherwise show an unstyled white
 * screen with no handler at all. Must render its own <html>/<body> because
 * the root layout is what just crashed.
 *
 * Reports directly with fetch rather than through clientLogger — if the root
 * layout is down, the less machinery between us and the report, the better.
 */
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  useEffect(() => {
    Sentry.captureException(error)
    try {
      void fetch('/api/client-errors', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          module: 'global-error',
          level: 'error',
          message: `${error.name}: ${error.message}${error.digest ? ` (digest ${error.digest})` : ''}${
            error.stack ? `\n${error.stack.split('\n').slice(0, 8).join('\n')}` : ''
          }`.slice(0, 4000),
          url: window.location?.href?.slice(0, 500) ?? null,
          userAgent: navigator?.userAgent?.slice(0, 300) ?? null,
        }),
        keepalive: true,
      }).catch(() => {})
    } catch {
      /* reporting must never make this worse */
    }
  }, [error])

  return (
    <html lang="en">
      <body
        style={{
          fontFamily: 'system-ui, sans-serif',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          margin: 0,
          backgroundColor: '#fafafa',
          color: '#18181b',
        }}
      >
        <div style={{ textAlign: 'center', padding: '2rem', maxWidth: '28rem' }}>
          <h1 style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>Something went wrong</h1>
          <p style={{ color: '#71717a', marginBottom: '1.5rem' }}>
            The app hit an unexpected error. It has been reported.
            {error.digest ? ` Reference: ${error.digest}` : ''}
          </p>
          <button
            onClick={reset}
            style={{
              padding: '0.5rem 1.25rem',
              borderRadius: '0.5rem',
              border: '1px solid #d4d4d8',
              background: '#18181b',
              color: '#fafafa',
              cursor: 'pointer',
              fontSize: '0.875rem',
            }}
          >
            Try again
          </button>
        </div>
      </body>
    </html>
  )
}
